<template>
  <div
    class="study-view-layout"
    :class="{ 'sv-dashboard-fullscreen': activeTab === 'viewdata' && dashboardFullscreen }"
  >
    <div
      v-if="studyLoading"
      class="study-loading-overlay"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div class="study-loading-card">
        <div class="study-loading-spinner" aria-hidden="true"></div>
        <div class="study-loading-title">Please wait</div>
        <div class="study-loading-message">Study data is being loaded.</div>
      </div>
    </div>

    <div
      v-if="maintenanceMessage && !studyLoading"
      class="loading load-error-state"
      role="alert"
      aria-live="assertive"
    >
      <p>{{ maintenanceMessage }}</p>
    </div>

    <header class="sv-header" v-if="!maintenanceMessage && !(activeTab === 'viewdata' && dashboardFullscreen)">
      <button class="btn-minimal back-btn" @click="goBackToDashboard" aria-label="Back to Dashboard">Back</button>
      <div class="title-wrap">
        <div class="sv-title-row">
          <h1 class="sv-title">{{ studyMeta.study_name || 'Study' }}</h1>
          <span v-if="isArchivedStudy" class="archived-badge">Archived</span>
        </div>
        <p class="sv-subtitle" v-if="hasValue(studyMeta.study_description)">{{ studyMeta.study_description }}</p>
      </div>
      <div class="header-spacer" aria-hidden="true"></div>
    </header>

    <div
      v-if="!maintenanceMessage"
      class="sv-content card"
      :class="{
        'sv-viewdata': activeTab === 'viewdata',
        'sv-compliance': activeTab === 'compliance',
        'sv-sidebar-collapsed': activeTab === 'viewdata' && dataSidebarCollapsed,
        'sv-dashboard-fullscreen': activeTab === 'viewdata' && dashboardFullscreen
      }"
    >
      <!-- Vertical Tabs -->
      <aside
        v-if="!(activeTab === 'viewdata' && dashboardFullscreen)"
        class="v-tabs"
        :class="{ collapsed: activeTab === 'viewdata' && dataSidebarCollapsed }"
        role="tablist"
        aria-orientation="vertical"
      >
        <!-- Collapse/Expand icon only for View Data tab -->
        <div class="v-tabs-tools" v-if="activeTab === 'viewdata'">
          <button
            class="btn-minimal icon-only"
            type="button"
            @click="toggleDataSidebar"
            :title="dataSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
            :aria-label="dataSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
          >
            <i :class="dataSidebarCollapsed ? icons.chevronRight : icons.chevronLeft"></i>
          </button>
        </div>

        <button
          v-for="t in visibleTabs"
          :key="t.key"
          :class="['v-tab', { active: activeTab === t.key }]"
          @click="activeTab = t.key"
          role="tab"
          :aria-selected="activeTab === t.key"
          :title="t.label"
        >
          {{ (activeTab === 'viewdata' && dataSidebarCollapsed) ? tabAbbrev(t.label) : t.label }}
        </button>
      </aside>

      <!-- Panels -->
      <section
        class="v-panel"
        :class="{
          'v-panel-tight': activeTab === 'viewdata',
          'v-panel-compliance': activeTab === 'compliance'
        }"
      >
        <!-- META-DATA -->
        <div v-if="activeTab === 'meta'">
          <h2 class="panel-title center">Meta-data</h2>

          <!-- Core meta (only show fields that have values) -->
          <div class="meta-grid">
            <div class="meta-item" v-if="hasValue(studyMeta.id)">
              <div class="meta-label">Study ID</div>
              <div class="meta-value monospace">{{ studyMeta.id }}</div>
            </div>
            <div class="meta-item" v-if="hasValue(studyMeta.study_name)">
              <div class="meta-label">Name</div>
              <div class="meta-value">{{ studyMeta.study_name }}</div>
            </div>
            <div class="meta-item" v-if="hasValue(studyMeta.study_description)">
              <div class="meta-label">Description</div>
              <div class="meta-value">{{ studyMeta.study_description }}</div>
            </div>
            <div class="meta-item" v-if="hasValue(studyMeta.created_by_display)">
              <div class="meta-label">Created By</div>
              <div class="meta-value">{{ studyMeta.created_by_display }}</div>
            </div>
            <div class="meta-item" v-if="hasValue(studyMeta.created_at)">
              <div class="meta-label">Created At</div>
              <div class="meta-value">{{ formatDateTime(studyMeta.created_at) }}</div>
            </div>
            <div class="meta-item" v-if="hasValue(studyMeta.updated_at)">
              <div class="meta-label">Updated At</div>
              <div class="meta-value">{{ formatDateTime(studyMeta.updated_at) }}</div>
            </div>
          </div>

          <!-- Collapsible meta sections (collapsed by default) -->
          <div class="meta-sections">
            <!-- Study Data -->
            <div class="meta-section">
              <div class="meta-section-head">
                <h3 class="sub-title">Study Data</h3>
                <button
                  class="btn-minimal icon-only meta-toggle"
                  type="button"
                  @click="toggleMetaSection('studyData')"
                  :title="metaCollapsed.studyData ? 'Expand' : 'Collapse'"
                  :aria-label="metaCollapsed.studyData ? 'Expand Study Data' : 'Collapse Study Data'"
                  :aria-expanded="(!metaCollapsed.studyData).toString()"
                >
                  <i :class="metaCollapsed.studyData ? icons.toggleDown : icons.toggleUp"></i>
                </button>
              </div>
              <div class="meta-section-body" v-show="!metaCollapsed.studyData">
                <div v-if="studyKVEntries.length" class="kv-grid">
                  <div class="kv-row" v-for="entry in studyKVEntries" :key="entry[0]">
                    <div class="kv-key">{{ prettyLabel(entry[0]) }}</div>
                    <div class="kv-val">{{ formatAny(entry[1]) }}</div>
                  </div>
                </div>
                <div v-else class="empty-state">No additional study data.</div>
              </div>
            </div>

            <!-- Groups -->
            <div class="meta-section">
              <div class="meta-section-head">
                <h3 class="sub-title">Groups</h3>
                <button
                  class="btn-minimal icon-only meta-toggle"
                  type="button"
                  @click="toggleMetaSection('groups')"
                  :title="metaCollapsed.groups ? 'Expand' : 'Collapse'"
                  :aria-label="metaCollapsed.groups ? 'Expand Groups' : 'Collapse Groups'"
                  :aria-expanded="(!metaCollapsed.groups).toString()"
                >
                  <i :class="metaCollapsed.groups ? icons.toggleDown : icons.toggleUp"></i>
                </button>
              </div>
              <div class="meta-section-body" v-show="!metaCollapsed.groups">
                <div v-if="groups && groups.length" class="list-grid">
                  <div class="list-card" v-for="(g, gi) in groups" :key="gi">
                    <div class="kv-row" v-for="entry in objectEntriesFiltered(g)" :key="entry[0]">
                      <div class="kv-key">{{ prettyLabel(entry[0]) }}</div>
                      <div class="kv-val">{{ formatAny(entry[1]) }}</div>
                    </div>
                  </div>
                </div>
                <div v-else class="empty-state">No groups defined.</div>
              </div>
            </div>

            <!-- Visits -->
            <div class="meta-section">
              <div class="meta-section-head">
                <h3 class="sub-title">Visits</h3>
                <button
                  class="btn-minimal icon-only meta-toggle"
                  type="button"
                  @click="toggleMetaSection('visits')"
                  :title="metaCollapsed.visits ? 'Expand' : 'Collapse'"
                  :aria-label="metaCollapsed.visits ? 'Expand Visits' : 'Collapse Visits'"
                  :aria-expanded="(!metaCollapsed.visits).toString()"
                >
                  <i :class="metaCollapsed.visits ? icons.toggleDown : icons.toggleUp"></i>
                </button>
              </div>
              <div class="meta-section-body" v-show="!metaCollapsed.visits">
                <div v-if="visits && visits.length" class="list-grid">
                  <div class="list-card" v-for="(v, vi) in visits" :key="vi">
                    <div class="kv-row" v-for="entry in objectEntriesFiltered(v)" :key="entry[0]">
                      <div class="kv-key">{{ prettyLabel(entry[0]) }}</div>
                      <div class="kv-val">{{ formatAny(entry[1]) }}</div>
                    </div>
                  </div>
                </div>
                <div v-else class="empty-state">No visits defined.</div>
              </div>
            </div>

            <!-- Subject Assignment -->
            <div class="meta-section">
              <div class="meta-section-head">
                <h3 class="sub-title">Subject Assignment</h3>
                <button
                  class="btn-minimal icon-only meta-toggle"
                  type="button"
                  @click="toggleMetaSection('assignment')"
                  :title="metaCollapsed.assignment ? 'Expand' : 'Collapse'"
                  :aria-label="metaCollapsed.assignment ? 'Expand Subject Assignment' : 'Collapse Subject Assignment'"
                  :aria-expanded="(!metaCollapsed.assignment).toString()"
                >
                  <i :class="metaCollapsed.assignment ? icons.toggleDown : icons.toggleUp"></i>
                </button>
              </div>
              <div class="meta-section-body" v-show="!metaCollapsed.assignment">
                <div class="kv-grid">
                  <div class="kv-row" v-if="hasValue(studyData.assignmentMethod)">
                    <div class="kv-key">Assignment Method</div>
                    <div class="kv-val">{{ studyData.assignmentMethod }}</div>
                  </div>
                  <div class="kv-row" v-if="hasValue(studyData.subjectCount)">
                    <div class="kv-key">Subjects Planned</div>
                    <div class="kv-val">{{ studyData.subjectCount }}</div>
                  </div>
                  <div class="kv-row" v-if="Array.isArray(subjects)">
                    <div class="kv-key">Subjects Enrolled</div>
                    <div class="kv-val">{{ subjects.length }}</div>
                  </div>
                  <div class="kv-row" v-if="assignmentSize">
                    <div class="kv-key">Assignment Matrix</div>
                    <div class="kv-val">
                      {{ assignmentSize.m }} × {{ assignmentSize.v }} × {{ assignmentSize.g }}
                      <span class="muted">(models × visits × groups)</span>
                    </div>
                  </div>
                </div>

                <!-- Per-Subject Group mapping (PI/Admin only) -->
                <div v-if="isPIOrAdmin" class="per-subject-block">
                  <h4 class="per-subject-title">Per-Subject Group Assignment</h4>
                  <div v-if="subjectAssignments.length" class="ps-table-wrap">
                    <table class="ps-table" aria-label="Per-subject group assignment">
                      <thead>
                        <tr>
                          <th scope="col">Subject ID</th>
                          <th scope="col">Assigned Group</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="row in subjectAssignments" :key="row.subjectId">
                          <td class="monospace">{{ row.subjectId }}</td>
                          <td>{{ row.groupName || '—' }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div v-else class="empty-state">No subjects enrolled yet.</div>
                </div>
              </div>
            </div>

            <!-- Template Versions -->
            <div class="meta-section">
              <div class="meta-section-head">
                <h3 class="sub-title">Template Versions</h3>
                <button
                  class="btn-minimal icon-only meta-toggle"
                  type="button"
                  @click="toggleMetaSection('versions')"
                  :title="metaCollapsed.versions ? 'Expand' : 'Collapse'"
                  :aria-label="metaCollapsed.versions ? 'Expand Template Versions' : 'Collapse Template Versions'"
                  :aria-expanded="(!metaCollapsed.versions).toString()"
                >
                  <i :class="metaCollapsed.versions ? icons.toggleDown : icons.toggleUp"></i>
                </button>
              </div>

              <div class="meta-section-body" v-show="!metaCollapsed.versions">
                <div class="versions-wrap">
                  <div v-if="versionsLoading" class="muted">Loading versions…</div>
                  <div v-else-if="!versions.length" class="empty-state">No template versions yet.</div>

                  <div v-else class="version-list">
                    <div class="version-card" v-for="(v, idx) in versions" :key="`ver-${v.version}`">
                      <div class="vc-head">
                        <div class="vc-title">
                          <span class="tag">v{{ v.version }}</span>
                          <span class="vc-date">{{ formatDateTime(v.created_at) }}</span>
                          <span v-if="idx === versions.length - 1" class="pill ok">Latest</span>
                        </div>

                        <div class="vc-summary" v-if="v.version > 1">
                          <template v-if="canShowVersionDetails && versionDiffs[v.version]">
                            <span class="muted">Changes from v{{ v.version - 1 }}:</span>
                            <span class="vc-summary-line">{{ versionDiffs[v.version].summary }}</span>
                          </template>
                          <template v-else>
                            <span class="muted">Changes from previous version are hidden (insufficient permission).</span>
                          </template>
                        </div>
                      </div>

                      <!-- Details -->
                      <details v-if="v.version > 1 && canShowVersionDetails && versionDiffs[v.version]">
                        <summary>Show details</summary>
                        <div class="vc-details diff-scroll">
                          <TemplateDiffView
                            :from="versionSchemas[v.version - 1]"
                            :to="versionSchemas[v.version]"
                            compact
                          />
                        </div>
                      </details>

                      <div v-else-if="v.version === 1" class="muted">Initial version.</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- COMPLIANCE VIEW -->
        <div v-else-if="activeTab === 'compliance'">
          <StudyComplianceView :study-id="studyId" :active="activeTab === 'compliance'" />
        </div>

        <!-- EDIT STUDY TAB -->
        <div v-else-if="activeTab === 'edit'">
          <h2 class="panel-title center">Edit Study</h2>

          <div v-if="isArchivedStudy" class="archive-state-card">
            <h3>This study is archived</h3>
            <p>Archived studies are read-only. Restore this study before changing its design, access, documents, or collected data.</p>
            <button
              v-if="canManageAccess"
              class="btn-primary"
              type="button"
              :disabled="archiveBusy"
              @click="unarchiveStudy"
            >
              {{ archiveBusy ? 'Restoring…' : 'Unarchive study' }}
            </button>
          </div>

          <template v-else>
            <p class="muted center" style="margin-top:-6px;">
              Choose exactly what you want to edit. Each step can be saved independently.
            </p>

            <div class="edit-steps-grid">
              <button
                v-for="s in editSteps"
                :key="s.displayStep"
                class="edit-step-card"
                @click="openEditStep(s.routeStep)"
                :disabled="!canEditStudy"
              >
                <div class="esc-title">{{ s.label }}</div>
                <div class="esc-desc">{{ s.desc }}</div>
              </button>
            </div>

            <div class="edit-hint muted">
              Tip: This will open the individual steps in edit mode with a dedicated Save button.
            </div>
            <div v-if="canManageAccess" class="edit-danger-zone">
              <button
                class="btn-minimal archive-study-btn"
                type="button"
                :disabled="archiveBusy"
                @click="openArchiveStudyDialog"
              >
                {{ archiveBusy ? 'Archiving…' : 'Archive Study' }}
              </button>
              <button
                class="btn-danger"
                type="button"
                @click="openDeleteStudyDialog"
                :disabled="deleteConfirm.busy"
              >
                Delete Study
              </button>
            </div>
          </template>
        </div>

        <!-- DOCUMENTS -->
        <div v-else-if="activeTab === 'docs'">
          <h2 class="panel-title center">Study Documents</h2>

          <h3 class="sub-title">Existing Study Attachments</h3>
          <div v-if="studyLevelFiles.length" class="doc-list">
            <div v-for="doc in studyLevelFiles" :key="doc.id || doc.file_name" class="doc-item">
              <div class="doc-name">{{ doc.file_name || doc.name || 'Document' }}</div>
              <div class="doc-meta muted">
                {{ doc.storage_option || 'file' }}
                <span v-if="doc.bytes || doc.size"> • </span>
                <span v-if="doc.bytes">{{ prettyBytes(doc.bytes) }}</span>
                <span v-else-if="doc.size">{{ prettyBytes(doc.size) }}</span>
              </div>
              <div v-if="isEditingFileDescription(doc)" class="doc-description-edit">
                <label :for="`study-file-description-${doc.id}`">Description</label>
                <textarea
                  :id="`study-file-description-${doc.id}`"
                  v-model="fileDescriptionDraft[fileDownloadKey(doc)]"
                  class="pi-desc-input"
                  rows="3"
                  placeholder="Add a short description…"
                  :disabled="isFileDescriptionBusy(doc)"
                ></textarea>
                <div class="doc-edit-actions">
                  <button
                    class="btn-primary compact"
                    type="button"
                    :disabled="isFileDescriptionBusy(doc)"
                    @click="saveFileDescription(doc)"
                  >
                    {{ isFileDescriptionBusy(doc) ? "Saving…" : "Save" }}
                  </button>
                  <button
                    class="btn-minimal compact"
                    type="button"
                    :disabled="isFileDescriptionBusy(doc)"
                    @click="cancelFileDescriptionEdit(doc)"
                  >
                    Cancel
                  </button>
                </div>
              </div>
              <div v-else-if="doc.description" class="doc-desc">{{ doc.description }}</div>
              <div v-else class="doc-desc muted">No description.</div>
              <div class="doc-path" :title="docPathTooltip(doc)">
                <span class="label">On disk:</span>
                <span class="file-path">{{ docRelativePath(doc) }}</span>
              </div>

              <div v-if="canDownloadStudyAttachment || canManageStudyAttachments" class="doc-actions">
                <button
                  v-if="canDownloadStudyAttachment"
                  class="btn-minimal"
                  type="button"
                  :disabled="isDownloadBusy(doc)"
                  @click="downloadAttachedFile(doc)"
                >
                  {{ isDownloadBusy(doc) ? "Downloading…" : "Download" }}
                </button>
                <button
                  v-if="canManageStudyAttachments && !isEditingFileDescription(doc)"
                  class="btn-minimal"
                  type="button"
                  :disabled="isFileDeleteBusy(doc)"
                  @click="startFileDescriptionEdit(doc)"
                >
                  {{ doc.description ? "Edit description" : "Add description" }}
                </button>
                <button
                  v-if="canManageStudyAttachments"
                  class="btn-minimal danger"
                  type="button"
                  :disabled="isFileDeleteBusy(doc) || isFileDescriptionBusy(doc)"
                  @click="openDocumentDeleteDialog(doc)"
                >
                  {{ isFileDeleteBusy(doc) ? "Deleting…" : "Delete" }}
                </button>
              </div>
            </div>
          </div>
          <div v-else class="empty-state">No study-level attachments yet.</div>

          <!-- Attach New Study Documents -->
          <div v-if="canManageStudyAttachments" class="subsection">
            <h3 class="sub-title">Attach New Study Documents</h3>

            <div class="attach-row">
              <FieldFileUpload
                :value="attachTempValue"
                :constraints="{ allowMultipleFiles: true, storagePreference: 'local' }"
                :readonly="uploading"
                stage="runtime"
                @input="onAttachTempChange"
                @file-selected="onFilesSelected"
              />
            </div>

            <div v-if="pendingFiles.length" class="pending-list">
              <div class="pending-item" v-for="(p, idx) in pendingFiles" :key="p.key">
                <div class="pi-head">
                  <div class="pi-name" :title="p.file.name">{{ p.file.name }}</div>
                  <div class="pi-size">{{ prettyBytes(p.file.size) }}</div>
                  <button class="icon-inline danger" type="button" @click="removePending(idx)" title="Remove">✕</button>
                </div>
                <label class="pi-desc-label">Description (optional)</label>
                <input
                  class="pi-desc-input"
                  type="text"
                  v-model="p.description"
                  :placeholder="'e.g., IRB approval PDF, protocol v2, consent form…'"
                  :disabled="uploading"
                />
              </div>
            </div>

            <div class="attach-actions">
              <button class="btn-primary" :disabled="!pendingFiles.length || uploading" @click="saveStudyAttachments">
                {{ uploading ? 'Saving…' : 'Save Attachments' }}
              </button>
            </div>
          </div>
        </div>

        <!-- SETTINGS / ACCESS MANAGEMENT -->
        <div v-else-if="activeTab === 'team'">
          <h2 class="panel-title center">Study Access Management</h2>

          <div class="subsection">
            <h3 class="sub-title">Access for this Study</h3>

            <div class="table-wrap">
              <table class="access-table" aria-label="Study access">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Permissions</th>
                    <th>Granted By</th>
                    <th>Granted At</th>
                    <th v-if="canManageAccess">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr class="owner-row">
                    <td class="strong">
                      {{ studyMeta.created_by_display || 'Owner' }}
                      <span class="tag">Owner</span>
                    </td>
                    <td>{{ studyMeta.created_by_email || '—' }}</td>
                    <td>PI / Admin</td>
                    <td>
                      <span class="pill ok">View</span>
                      <span class="pill ok">Add data</span>
                      <span class="pill ok">Edit study</span>
                    </td>
                    <td>System</td>
                    <td>{{ formatDateTime(studyMeta.created_at) || '—' }}</td>
                    <td v-if="canManageAccess">—</td>
                  </tr>

                  <tr v-for="g in accessList" :key="g.user_id">
                    <td class="strong">{{ g.display_name || g.username || g.email || ('User#' + g.user_id) }}</td>
                    <td>{{ g.email || '—' }}</td>
                    <td>{{ prettyGrantRole(g.role) }}</td>
                    <td>
                      <span class="pill" :class="g.permissions?.view ? 'ok' : 'muted'">View</span>
                      <span class="pill" :class="g.permissions?.add_data ? 'ok' : 'muted'">Add data</span>
                      <span class="pill" :class="g.permissions?.edit_study ? 'ok' : 'muted'">Edit study</span>
                    </td>
                    <td>{{ g.granted_by_display || g.granted_by || '—' }}</td>
                    <td>{{ formatDateTime(g.created_at) || '—' }}</td>
                    <td v-if="canManageAccess">
                      <button class="btn-link danger" :disabled="revokeBusy[g.user_id]" @click="openRevokeDialog(g)" title="Revoke access">Revoke</button>
                    </td>
                  </tr>

                  <tr v-if="!accessList.length">
                    <td :colspan="canManageAccess ? 7 : 6" class="muted center">No additional users have access.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Revoke confirm dialog -->
          <div v-if="confirm.visible" class="modal-backdrop" role="dialog" aria-modal="true" @click.self="cancelRevoke">
            <div class="modal" @keydown.esc.prevent="cancelRevoke" tabindex="-1">
              <h3 class="modal-title">Revoke access?</h3>
              <p class="modal-text">
                This will remove access for
                <span class="strong">
                  {{ confirm.target?.display_name || confirm.target?.email || ('User#' + confirm.target?.user_id) }}
                </span>.
              </p>
              <div class="modal-actions">
                <button class="btn-minimal" @click="cancelRevoke">Cancel</button>
                <button class="btn-primary danger" :disabled="revokeBusy[confirm.target?.user_id]" @click="confirmRevoke">
                  {{ revokeBusy[confirm.target?.user_id] ? 'Revoking…' : 'Revoke' }}
                </button>
              </div>
            </div>
          </div>

          <!-- GRANT FORM -->
          <div v-if="canManageAccess" class="subsection">
            <h3 class="sub-title">Grant Access</h3>

            <div class="grant-grid access-grant-grid">
              <div class="field grant-user-field">
                <label class="label">Select User</label>
                <select v-model="selectedUserId" class="input input-select">
                  <option value="" disabled>Select a user…</option>
                  <option v-for="u in grantableUsers" :key="u.id" :value="u.id">
                    {{ userDisplay(u) }}
                  </option>
                </select>
                <div class="help" v-if="!allUsers.length">No users found to grant.</div>
              </div>

              <div class="field grant-perm-field">
                <label class="label">Permissions</label>
                <div class="perm-row">
                  <label class="chk">
                    <input type="checkbox" v-model="permView" />
                    <span>View</span>
                  </label>
                  <label class="chk">
                    <input type="checkbox" v-model="permAdd" />
                    <span>Add Data</span>
                  </label>
                  <label class="chk">
                    <input type="checkbox" v-model="permEdit" />
                    <span>Edit Study</span>
                  </label>
                </div>
                <div class="help">
                  Choose exactly what this user can do for this study.
                </div>
              </div>

              <div class="actions grant-actions">
                <button
                  class="btn-primary"
                  :disabled="!selectedUserId || granting"
                  @click="grantAccess"
                >
                  {{ granting ? 'Granting…' : 'Grant Access' }}
                </button>
              </div>
            </div>
          </div>

          <!-- STUDY CONFIGURATIONS (placeholder) -->
          <div class="subsection">
            <h3 class="sub-title">Study Configurations</h3>
            <p class="muted">
              (Yet to Decide on this part) This section can include configuration options such as: edit windows for visits, allowed data entry roles,
              randomization settings, subject ID format, data export preferences, and notification settings.
            </p>
          </div>
        </div>

        <!-- AUDIT LOGS -->
        <div v-else-if="activeTab === 'audit'">
          <StudyAuditLogs :study-id="studyId" />
        </div>

        <!-- EXPORT OPTIONS TAB (single tab; export screen appears inside it) -->
        <div v-else-if="activeTab === 'bids'">
          <h2 class="panel-title center">Export Options</h2>

          <!-- Embedded ExportStudy in SAME container -->
          <div v-if="showExportTemplate">

            <div class="subsection" style="margin-top: 12px;">
              <ExportStudy :embedded="true" :study-id="studyId" />
            </div>
          </div>

          <!-- Default Export Options screen -->
          <template v-else>
            <div class="subsection">
              <h3 class="sub-title">Study exports</h3>
              <p class="muted">
                Prepare analysis-ready data, a focused study package, or a reusable template.
              </p>

              <div class="study-export-grid">
                <button v-if="canDownloadFullStudy" class="main-export-option" type="button" :disabled="downloadingFullStudyZip" @click="showDownloadDialog = true">
                  <span class="export-option-icon"><i class="fas fa-download"></i></span>
                  <span class="export-option-copy">
                    <span class="export-option-label">RECOMMENDED</span>
                    <strong>{{ downloadingFullStudyZip ? "Preparing…" : "Download Study" }}</strong>
                    <small>BIDS-compliant study by default, with subject, group, visit, file, audit, and version controls.</small>
                    <span class="export-option-action">Choose download options <i class="fas fa-arrow-right"></i></span>
                  </span>
                </button>
                <div class="secondary-export-options">
                  <button type="button" @click="exportStudyTemplate">
                    <span class="secondary-export-icon"><i class="fas fa-file-export"></i></span>
                    <span><strong>Export Study Template</strong><small>Reusable study structure without collected data</small></span>
                    <i class="fas fa-chevron-right"></i>
                  </button>
                  <button v-if="canDownloadFullStudy" type="button" :disabled="downloadingMergeZip" @click="downloadStudyZip">
                    <span class="secondary-export-icon"><i class="fas fa-exchange-alt"></i></span>
                    <span><strong>{{ downloadingMergeZip ? "Preparing…" : "Download Study For Merge" }}</strong><small>Template and CSV bundle for importing elsewhere</small></span>
                    <i class="fas fa-chevron-right"></i>
                  </button>
                </div>
                <div v-if="!canDownloadFullStudy" class="muted export-permission-note">
                  Only the study owner or an administrator can download the full study.
                </div>
              </div>
            </div>

            <!-- Existing dataset (kept as-is) -->
            <div v-if="canSeeBidsButton" class="subsection bids-location">
              <h3 class="sub-title">Dataset location</h3>

              <p class="muted">
                Dataset folder on disk for this study:
                <span v-if="bidsDatasetPath">
                  <code class="file-path">{{ bidsDatasetPath }}</code>
                  <span v-if="!bidsPathExists" class="muted"> (folder does not exist yet)</span>
                </span>
                <span v-else>
                  Not available yet.
                </span>
              </p>

              <div class="bids-actions">
                <button
                  class="btn-primary"
                  type="button"
                  :disabled="openingBids || !bidsDatasetPath || !bidsPathExists"
                  @click="openBidsFolder"
                >
                  {{ openingBids ? 'Opening…' : 'Open Dataset Folder' }}
                </button>
              </div>
            </div>

            <p v-else class="muted">
              Only the study owner or an administrator can view the BIDS dataset location.
            </p>
          </template>
        </div>

        <!-- Keep View Data mounted after its first visit so switching tabs does not rebuild it. -->
        <div v-show="activeTab === 'viewdata'" class="viewdata-host">
          <StudyDataDashboard
            v-if="viewDataInitialized && sharedStudyDataReady"
            :key="`viewdata-${studyId}`"
            :study-id="studyId"
            :embedded="true"
            :active="activeTab === 'viewdata'"
            :fullscreen="dashboardFullscreen"
            :initial-study="studyRecord"
            :initial-versions="versions"
            :initial-study-files="allFiles"
            @toggle-fullscreen="toggleDashboardFullscreen"
          />
        </div>
      </section>
    </div>
    <!-- Archive study confirm dialog -->
    <div
      v-if="archiveConfirm.visible"
      class="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="archive-study-dialog-title"
      @click.self="cancelArchiveStudy"
    >
      <div class="modal" tabindex="-1" @keydown.esc.prevent="cancelArchiveStudy">
        <h3 id="archive-study-dialog-title" class="modal-title">Archive this study?</h3>
        <p class="modal-text">It will become read-only until it is unarchived.</p>
        <div class="modal-actions">
          <button
            class="btn-minimal"
            type="button"
            :disabled="archiveBusy"
            @click="cancelArchiveStudy"
          >
            Cancel
          </button>
          <button
            class="btn-primary"
            type="button"
            :disabled="archiveBusy"
            @click="archiveStudy"
          >
            {{ archiveBusy ? "Archiving…" : "Archive Study" }}
          </button>
        </div>
      </div>
    </div>
    <!-- Delete study document confirm dialog -->
    <div
      v-if="documentDeleteConfirm.visible"
      class="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-document-dialog-title"
      @click.self="cancelDocumentDelete"
    >
      <div class="modal" tabindex="-1" @keydown.esc.prevent="cancelDocumentDelete">
        <h3 id="delete-document-dialog-title" class="modal-title">Delete document?</h3>
        <p class="modal-text">
          This will permanently delete
          <span class="strong">{{ documentDeleteName }}</span>
          from the study.
        </p>
        <p class="modal-text" style="margin-top:-6px;">
          This action cannot be undone.
        </p>
        <div class="modal-actions">
          <button
            class="btn-minimal"
            type="button"
            :disabled="documentDeleteConfirm.busy"
            @click="cancelDocumentDelete"
          >
            Cancel
          </button>
          <button
            class="btn-primary danger"
            type="button"
            :disabled="documentDeleteConfirm.busy"
            @click="confirmDocumentDelete"
          >
            {{ documentDeleteConfirm.busy ? "Deleting…" : "Delete Document" }}
          </button>
        </div>
      </div>
    </div>
    <!-- Delete study confirm dialog -->
    <div
      v-if="deleteConfirm.visible"
      class="modal-backdrop"
      role="dialog"
      aria-modal="true"
      @click.self="cancelDeleteStudy"
    >
      <div class="modal" tabindex="-1">
        <h3 class="modal-title">Delete study?</h3>
        <p class="modal-text">
          This will permanently delete
          <span class="strong">{{ studyMeta.study_name || "this study" }}</span>
          from both the database and the study filesystem.
        </p>
        <p class="modal-text" style="margin-top:-6px;">
          This action cannot be undone.
        </p>
        <div class="modal-actions">
          <button class="btn-minimal" @click="cancelDeleteStudy" :disabled="deleteConfirm.busy">
            Cancel
          </button>
          <button class="btn-primary danger" @click="confirmDeleteStudy" :disabled="deleteConfirm.busy">
            {{ deleteConfirm.busy ? "Deleting…" : "Delete Study" }}
          </button>
        </div>
      </div>
    </div>
    <StudyDownloadDialog
      :open="showDownloadDialog"
      :downloading="downloadingFullStudyZip"
      :subjects="subjects"
      :groups="groups"
      :visits="visits"
      :versions="versions"
      @close="showDownloadDialog = false"
      @download="downloadFullStudyZip"
    />
  </div>
</template>

<script>
import axios from "axios";
import FieldFileUpload from "@/components/fields/FieldFileUpload.vue";
import StudyAuditLogs from "@/components/StudyAuditLogs.vue";
import TemplateDiffView, { computeTemplateDiff, buildVersionSummary } from "@/components/TemplateDiffView.vue";
import StudyDataDashboard from "@/components/StudyDataDashboard.vue";
import StudyComplianceView from "@/components/StudyComplianceView.vue";
import icons from "@/assets/styles/icons";
import { downloadStudyBundle } from "@/utils/studyDownload";
import ExportStudy from "@/components/ExportStudy.vue";
import StudyDownloadDialog from "@/components/StudyDownloadDialog.vue";

export default {
  name: "StudyView",
  components: { FieldFileUpload, StudyAuditLogs, TemplateDiffView, StudyDataDashboard, StudyComplianceView, ExportStudy, StudyDownloadDialog },
  data() {
    return {
      deleteConfirm: {
        visible: false,
        busy: false,
      },
      documentDeleteConfirm: {
        visible: false,
        busy: false,
        target: null,
      },
      studyLoading: true,
      maintenanceMessage: "",
      studyId: this.$route.params.id,
      studyRecord: null,
      sharedStudyDataReady: false,

      // meta/content
      studyMeta: {
        id: null,
        study_name: "",
        study_description: "",
        created_by_id: null,
        created_by_display: "",
        created_by_email: "",
        created_at: "",
        updated_at: "",
        permissions: null,
        status: "",
        archived_from_status: null,
      },
      studyData: {},
      me: null,
      confirm: { visible: false, target: null },
      archiveConfirm: { visible: false },
      archiveBusy: false,

      icons,

      // Meta tab collapsibles (collapsed by default)
      metaCollapsed: {
        studyData: true,
        groups: true,
        visits: true,
        assignment: true,
        versions: true,
      },

      // View Data embedding state
      dataSidebarCollapsed: false,
      dashboardFullscreen: false,
      viewDataInitialized: false,

      // tabs
      activeTab: "meta",
      tabs: [
        { key: "meta", label: "Meta-data" },
        { key: "compliance", label: "Compliance view" },
        { key: "edit", label: "Edit Study", requiresEdit: true },
        { key: "docs", label: "Documents" },
        { key: "team", label: "Study Access" },
        { key: "viewdata", label: "View Data" },
        { key: "audit", label: "Audit logs" },
        { key: "bids", label: "Export Options" },
      ],

      // Export template embedding state (NO new tab, no route)
      showExportTemplate: false,
      showDownloadDialog: false,

      //  7-step edit launcher (kept)
      editSteps: [
        { displayStep: 1, routeStep: 1, label: "1) Study details", desc: "Title, description, basics" },
        { displayStep: 2, routeStep: 2, label: "2) Groups", desc: "Treatment arms, groups, labels" },
        { displayStep: 3, routeStep: 3, label: "3) Subject setup", desc: "Subject count and assignment method" },
        { displayStep: 4, routeStep: 4, label: "4) Group assignment", desc: "Assign subjects to groups" },
        { displayStep: 5, routeStep: 5, label: "5) Visits", desc: "Visit definitions, windows, order" },
        { displayStep: 6, routeStep: 6, label: "6) Forms", desc: "Forms, sections, fields (templates)" },
      ],

      // files
      allFiles: [],
      attachTempValue: [],
      pendingFiles: [],
      uploading: false,
      fileDownloadBusy: {},
      fileDescriptionDraft: {},
      fileDescriptionBusy: {},
      fileDeleteBusy: {},

      // access management
      accessList: [],
      allUsers: [],
      selectedUserId: "",
      permView: true,
      permAdd: true,
      permEdit: false,
      granting: false,
      revokeBusy: {},

      // versions
      versionsLoading: false,
      versions: [],
      versionSchemas: {},
      versionDiffs: {},
      canShowVersionDetails: true,
      versionDetailsLoaded: false,

      bidsDatasetPath: "",
      bidsPathExists: false,
      openingBids: false,


      downloadingMergeZip: false,
      downloadingFullStudyZip: false,
    };
  },
  computed: {
    // --- permission object helpers (Option A shape: resp.data.metadata.permissions) ---
    studyPerms() {
      const p = this.studyMeta?.permissions;
      return p && typeof p === "object" ? p : null;
    },
    canDownloadFullStudy() {
      return this.isAdmin || this.isOwner;
    },
    hasEditStudyPermission() {
      const p = this.studyPerms;
      return p ? p.edit_study === true : false;
    },

    visibleTabs() {
      if (this.isArchivedStudy) {
        const archivedTabs = new Set(["meta", "compliance", "viewdata", "bids"]);
        if (this.canManageAccess) archivedTabs.add("edit");
        return (this.tabs || []).filter(t => archivedTabs.has(t.key));
      }
      return (this.tabs || []).filter(t => !t.requiresEdit || this.canEditStudy);
    },

    groups() { return Array.isArray(this.studyData.groups) ? this.studyData.groups : []; },
    visits() { return Array.isArray(this.studyData.visits) ? this.studyData.visits : []; },
    subjects() { return Array.isArray(this.studyData.subjects) ? this.studyData.subjects : []; },
    assignmentSize() {
      const a = this.studyData.assignments;
      if (!Array.isArray(a) || !a.length) return null;
      const m = a.length;
      const v = Array.isArray(a[0]) ? a[0].length : 0;
      const g = v && Array.isArray(a[0][0]) ? a[0][0].length : 0;
      return { m, v, g };
    },
    studyKVEntries() { return this.objectEntriesFiltered(this.studyData.study); },

    studyLevelFiles() {
      const arr = Array.isArray(this.allFiles) ? this.allFiles : [];
      return arr.filter(f => f.subject_index == null && f.visit_index == null && f.group_index == null);
    },

    token() { return this.$store.state.token; },

    // roles
    myRoleRaw() { return (this.me && (this.me.profile?.role || this.me.role)) || ""; },
    myRole() { return String(this.myRoleRaw).trim(); },
    isAdmin() { return this.myRole.toLowerCase() === "administrator"; },
    isPI() {
      const r = this.myRole.toLowerCase();
      return r === "principal investigator" || r === "pi";
    },
    isPIOrAdmin() { return this.isAdmin || this.isPI; },

    // ownership + capabilities
    isOwner() {
      const meId = this.me?.id;
      return meId != null && this.studyMeta.created_by_id != null && Number(meId) === Number(this.studyMeta.created_by_id);
    },

    isArchivedStudy() {
      return String(this.studyMeta?.status || "").trim().toUpperCase() === "ARCHIVED";
    },

    // FIX: Edit Study tab visibility/control = Admin OR Owner OR edit_study permission
    canEditStudy() { return !this.isArchivedStudy && (this.isAdmin || this.isOwner || this.hasEditStudyPermission); },

    canManageAccess() { return this.isAdmin || this.isOwner; },
    canSeeBidsButton() { return this.isAdmin || this.isOwner; },
    canManageStudyAttachments() {
      if (this.isArchivedStudy) return false;
      if (this.isAdmin || this.isOwner) return true;
      return this.studyPerms?.add_data === true;
    },

    // download attached study file access:
    // owner/admin OR grant with all 3 permissions
    canDownloadStudyAttachment() {
      if (this.isAdmin || this.isOwner) return true;

      const p = this.studyPerms;
      if (!p) return false;

      return p.view === true && p.add_data === true && p.edit_study === true;
    },

    // per-subject table
    subjectAssignments() {
      const subs = this.subjects || [];
      return subs.map((s) => ({ subjectId: s?.id || "", groupName: s?.group || "" }));
    },

    grantableUsers() {
      const grantedIds = new Set(this.accessList.map(g => g.user_id));
      if (this.studyMeta.created_by_id != null) grantedIds.add(Number(this.studyMeta.created_by_id));
      return (this.allUsers || [])
        .filter(u => u && u.id != null && !grantedIds.has(Number(u.id)))
        .sort((a, b) => this.userDisplay(a).localeCompare(this.userDisplay(b)));
    },
    documentDeleteName() {
      const doc = this.documentDeleteConfirm.target;
      return doc?.file_name || doc?.name || "this document";
    },
  },
  watch: {
    activeTab(val) {
      if (this.studyMeta?.id && !this.visibleTabs.some((tab) => tab.key === val)) {
        this.activeTab = "meta";
        return;
      }
      // Guard: if someone deep-links to ?tab=edit but can't edit, bounce to meta
      if (val === "edit" && !this.canEditStudy && !(this.isArchivedStudy && this.canManageAccess)) {
        this.activeTab = "meta";
        return;
      }

      if (val === "viewdata") {
        this.dataSidebarCollapsed = true;
        this.viewDataInitialized = true;
      } else {
        this.dataSidebarCollapsed = false;
        this.dashboardFullscreen = false;
      }

      // ensure export template doesn't "stick" when you leave Export Options
      if (val !== "bids") this.showExportTemplate = false;

      if (val === "team") this.ensureAccessData();
      if (val === "bids" && this.canSeeBidsButton) this.fetchBidsPath();

      this.$router
        .replace({ query: { ...this.$route.query, tab: val } })
        .catch(() => null);
    },
  },
  methods: {
    openArchiveStudyDialog() {
      if (!this.canManageAccess || this.isArchivedStudy || this.archiveBusy) return;
      this.archiveConfirm.visible = true;
    },

    cancelArchiveStudy() {
      if (this.archiveBusy) return;
      this.archiveConfirm.visible = false;
    },

    async archiveStudy() {
      if (!this.archiveConfirm.visible || !this.canManageAccess || this.isArchivedStudy || this.archiveBusy) return;

      this.archiveBusy = true;
      try {
        await axios.post(
          `/forms/studies/${this.studyId}/archive`,
          {},
          { headers: { Authorization: `Bearer ${this.token}` } }
        );
        this.archiveConfirm.visible = false;
        await this.fetchStudy();
      } catch (e) {
        alert(e?.response?.data?.detail || "Failed to archive the study.");
      } finally {
        this.archiveBusy = false;
      }
    },

    async unarchiveStudy() {
      if (!this.canManageAccess || !this.isArchivedStudy || this.archiveBusy) return;
      this.archiveBusy = true;
      try {
        await axios.post(
          `/forms/studies/${this.studyId}/unarchive`,
          {},
          { headers: { Authorization: `Bearer ${this.token}` } }
        );
        await this.fetchStudy();
      } catch (e) {
        alert(e?.response?.data?.detail || "Failed to unarchive the study.");
      } finally {
        this.archiveBusy = false;
      }
    },

    openDeleteStudyDialog() {
      this.deleteConfirm.visible = true;
    },

    cancelDeleteStudy() {
      if (this.deleteConfirm.busy) return;
      this.deleteConfirm.visible = false;
    },

    async confirmDeleteStudy() {
      const token = this.token;
      if (!token) {
        this.$router.push("/login");
        return;
      }

      this.deleteConfirm.busy = true;
      try {
        await axios.delete(`/forms/studies/${this.studyId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        this.deleteConfirm.visible = false;
        this.$router.push({ name: "Dashboard", query: { openStudies: "true" } });
      } catch (e) {
        console.error("Failed to delete study:", e?.response?.data || e.message);
        alert("Failed to delete study.");
      } finally {
        this.deleteConfirm.busy = false;
      }
    },
    tabAbbrev(label) {
      const s = String(label || "").trim();
      if (!s) return "•";
      const words = s.split(/\s+/).filter(Boolean);
      if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
      return (words[0][0] + words[1][0]).toUpperCase();
    },
    toggleDataSidebar() {
      this.dataSidebarCollapsed = !this.dataSidebarCollapsed;
    },
    toggleDashboardFullscreen() {
      if (this.activeTab !== "viewdata") return;
      this.dashboardFullscreen = !this.dashboardFullscreen;
    },

    toggleMetaSection(key) {
      if (!key || !Object.prototype.hasOwnProperty.call(this.metaCollapsed, key)) return;
      const isOpening = this.metaCollapsed[key] === true;
      this.metaCollapsed[key] = !this.metaCollapsed[key];
      if (key === "versions" && isOpening && !this.versionDetailsLoaded) {
        this.fetchVersionDiffDetails();
      }
    },

    // ExportStudy shown in SAME container; no back button + no extra header
    exportStudyTemplate() {
      this.showExportTemplate = true;
    },
    async downloadStudyZip() {
      const token = this.token;
      if (!token) {
        alert("Please log in again.");
        this.$router.push("/login");
        return;
      }

      this.downloadingMergeZip = true;
      try {
        await downloadStudyBundle({ studyId: this.studyId, token });
      } catch (e) {
        console.error("Failed to download study bundle for merge:", e);
        alert("Failed to download study bundle for merge.");
      } finally {
        this.downloadingMergeZip = false;
      }
    },

    async downloadFullStudyZip(options = {}) {
      const token = this.token;
      if (!token) {
        alert("Please log in again.");
        this.$router.push("/login");
        return;
      }

      this.downloadingFullStudyZip = true;
      try {
        const response = await axios.get(
          `/forms/studies/${this.studyId}/export`,
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: "blob",
            params: options,
          }
        );

        const blob = new Blob([response.data], { type: "application/zip" });
        const url = window.URL.createObjectURL(blob);

        const disposition = response.headers?.["content-disposition"] || "";
        let filename = `study_${this.studyId}.zip`;

        const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
        const asciiMatch = disposition.match(/filename="?([^"]+)"?/i);

        if (utf8Match && utf8Match[1]) {
          filename = decodeURIComponent(utf8Match[1]);
        } else if (asciiMatch && asciiMatch[1]) {
          filename = asciiMatch[1];
        }

        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();

        window.URL.revokeObjectURL(url);
        this.showDownloadDialog = false;
      } catch (e) {
        console.error("Failed to download full study:", e?.response?.data || e.message);
        alert("Failed to download full study.");
      } finally {
        this.downloadingFullStudyZip = false;
      }
    },

    // tiny helpers
    userDisplay(u) {
      const firstLast = [u.first_name || u.profile?.first_name, u.last_name || u.profile?.last_name].filter(Boolean).join(" ").trim();
      return u.name || u.full_name || firstLast || u.username || u.email || `User#${u.id}`;
    },
    prettyGrantRole(role) {
      if (!role) return "—";
      const r = String(role).toLowerCase().trim();

      if (r === "principal investigator" || r === "pi") return "Principal Investigator";
      if (r === "administrator" || r === "admin") return "Administrator";
      if (r === "investigator") return "Investigator";
      if (r === "no access") return "No Access";
      return role;
    },

    // layout helpers
    makeDatasetFolder(studyId, studyName) {
      const alnum = (s) => String(s || "").replace(/[^A-Za-z0-9]/g, "");
      const base = (studyName || `study${studyId}`).replace(/ /g, "");
      const slug = alnum(base).slice(0, 48);
      return slug ? `study_${studyId}_${slug}` : `study_${studyId}`;
    },
    docRelativePath(file) {
      const folder = this.makeDatasetFolder(this.studyMeta.id, this.studyMeta.study_name);
      const fname = file?.file_name || "";
      return `bids_datasets/${folder}/metadata/${fname}`;
    },
    docPathTooltip(file) {
      return `Location on disk (relative to backend working directory): ${this.docRelativePath(file)}`;
    },

    fileDownloadKey(doc) {
      if (!doc) return "";
      return String(doc.id || doc.file_id || doc.file_name || doc.name || Math.random());
    },

    isDownloadBusy(doc) {
      const key = this.fileDownloadKey(doc);
      return !!this.fileDownloadBusy[key];
    },

    isEditingFileDescription(doc) {
      return Object.prototype.hasOwnProperty.call(
        this.fileDescriptionDraft,
        this.fileDownloadKey(doc)
      );
    },

    isFileDescriptionBusy(doc) {
      return !!this.fileDescriptionBusy[this.fileDownloadKey(doc)];
    },

    isFileDeleteBusy(doc) {
      return !!this.fileDeleteBusy[this.fileDownloadKey(doc)];
    },

    startFileDescriptionEdit(doc) {
      const key = this.fileDownloadKey(doc);
      this.fileDescriptionDraft = {
        ...this.fileDescriptionDraft,
        [key]: doc?.description || "",
      };
    },

    cancelFileDescriptionEdit(doc) {
      const key = this.fileDownloadKey(doc);
      const next = { ...this.fileDescriptionDraft };
      delete next[key];
      this.fileDescriptionDraft = next;
    },

    async saveFileDescription(doc) {
      const key = this.fileDownloadKey(doc);
      const token = this.token;
      if (!token) {
        this.$router.push("/login");
        return;
      }

      this.fileDescriptionBusy = { ...this.fileDescriptionBusy, [key]: true };
      try {
        const { data } = await axios.patch(
          `/forms/studies/${this.studyId}/files/${doc.id}`,
          { description: this.fileDescriptionDraft[key] || "" },
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { audit_label: "Update Study Document Description" },
          }
        );
        const index = this.allFiles.findIndex((file) => Number(file.id) === Number(doc.id));
        if (index >= 0) this.allFiles.splice(index, 1, data);
        this.cancelFileDescriptionEdit(doc);
      } catch (err) {
        console.error("Description update failed:", err?.response?.data || err.message);
        alert(this.apiErrorDetail(err) || "Failed to update the document description.");
      } finally {
        const next = { ...this.fileDescriptionBusy };
        delete next[key];
        this.fileDescriptionBusy = next;
      }
    },

    openDocumentDeleteDialog(doc) {
      this.documentDeleteConfirm = {
        visible: true,
        busy: false,
        target: doc,
      };
      this.$nextTick(() => {
        const dialogs = this.$el?.querySelectorAll(".modal");
        dialogs?.[dialogs.length - 1]?.focus();
      });
    },

    cancelDocumentDelete() {
      if (this.documentDeleteConfirm.busy) return;
      this.documentDeleteConfirm = { visible: false, busy: false, target: null };
    },

    async confirmDocumentDelete() {
      const doc = this.documentDeleteConfirm.target;
      if (!doc) return;
      const key = this.fileDownloadKey(doc);
      const token = this.token;
      if (!token) {
        this.$router.push("/login");
        return;
      }

      this.documentDeleteConfirm.busy = true;
      this.fileDeleteBusy = { ...this.fileDeleteBusy, [key]: true };
      try {
        await axios.delete(`/forms/studies/${this.studyId}/files/${doc.id}`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { audit_label: "Delete Study Document" },
        });
        this.allFiles = this.allFiles.filter((file) => Number(file.id) !== Number(doc.id));
        this.cancelFileDescriptionEdit(doc);
        this.documentDeleteConfirm = { visible: false, busy: false, target: null };
      } catch (err) {
        console.error("Document deletion failed:", err?.response?.data || err.message);
        alert(this.apiErrorDetail(err) || "Failed to delete the study document.");
        this.documentDeleteConfirm.busy = false;
      } finally {
        const next = { ...this.fileDeleteBusy };
        delete next[key];
        this.fileDeleteBusy = next;
      }
    },

    async downloadAttachedFile(doc) {
      if (!doc) return;

      const key = this.fileDownloadKey(doc);
      this.fileDownloadBusy = {
        ...this.fileDownloadBusy,
        [key]: true,
      };

      try {
        const token = this.token;
        if (!token) {
          this.$router.push("/login");
          return;
        }

        const response = await axios.get(
          `/forms/studies/${this.studyId}/files/${doc.id}/download`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            responseType: "blob",
          }
        );

        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = doc.file_name || "download";

        document.body.appendChild(link);
        link.click();
        link.remove();

        window.URL.revokeObjectURL(url);
      } catch (err) {
        console.error("Download failed:", err?.response?.data || err.message);
        alert("Failed to download file");
      } finally {
        const next = { ...this.fileDownloadBusy };
        delete next[key];
        this.fileDownloadBusy = next;
      }
    },

    scrollToTop() {
      try {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
        if (this.$el && typeof this.$el.scrollTop === "number") this.$el.scrollTop = 0;
      } catch (err) {
        console.warn("StudyView scrollToTop failed:", err);
      }
    },
    goBackToDashboard() {
      this.$router.push({ name: "Dashboard", query: { openStudies: "true" } });
    },
    formatDateTime(d) {
      if (!d) return "";
      return new Date(d).toLocaleString("en-GB", {
        year: "numeric", month: "short", day: "numeric",
        hour: "2-digit", minute: "2-digit", second: "2-digit",
      });
    },
    prettyBytes(n) {
      if (n === 0) return "0 B";
      if (!n && n !== 0) return "";
      const units = ["B","KB","MB","GB","TB"];
      const i = Math.floor(Math.log(n) / Math.log(1024));
      return `${(n / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
    },
    prettyLabel(key) {
      if (!key) return "";
      return String(key)
        .replace(/[_-]+/g, " ")
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .replace(/\s+/g, " ")
        .replace(/^./, s => s.toUpperCase());
    },
    formatAny(val) {
      if (val == null) return "—";
      if (typeof val === "boolean") return val ? "Yes" : "No";
      if (Array.isArray(val)) return val.length ? JSON.stringify(val) : "—";
      if (typeof val === "object") return Object.keys(val).length ? JSON.stringify(val) : "—";
      const s = String(val);
      return s.trim().length ? s : "—";
    },
    hasValue(val) {
      if (val === null || val === undefined) return false;
      if (typeof val === "string") return val.trim().length > 0;
      if (Array.isArray(val)) return val.length > 0;
      if (typeof val === "object") return Object.keys(val).length > 0;
      return true;
    },
    objectEntriesFiltered(obj) {
      return Object.entries(obj || {}).filter(([, v]) => this.hasValue(v));
    },

    async fetchMe() {
      const token = this.token;
      if (!token) return;
      try {
        const { data } = await axios.get("/users/me", { headers: { Authorization: `Bearer ${token}` } });
        this.me = data || null;
      } catch (e) {
        console.warn("Failed to fetch /users/me:", e?.response?.data || e.message);
      }
    },
    async fetchUserNameById(userId) {
      const token = this.token;
      if (!token || userId == null) return null;
      try {
        const { data } = await axios.get(`/users/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
        const u = data || {};
        const firstLast = [u?.profile?.first_name, u?.profile?.last_name].filter(Boolean).join(" ").trim();
        return u.name || u.full_name || firstLast || u.username || u.email || null;
      } catch (e) {
        return null;
      }
    },
    async fetchStudy() {
      const token = this.token;
      if (!token) { this.$router.push("/login"); return; }
      try {
        this.maintenanceMessage = "";
        const resp = await axios.get(`/forms/studies/${this.studyId}`, { headers: { Authorization: `Bearer ${token}` } });
        this.studyRecord = resp.data || null;
        const sd = resp.data?.content?.study_data || {};
        const meta = resp.data?.metadata || {};

        let createdByDisplay =
          meta.created_by_name ||
          meta.created_by_full_name ||
          meta.created_by_display ||
          meta.created_by_username ||
          meta.created_by_email ||
          null;

        if (!createdByDisplay && meta.created_by != null) {
          const resolved = await this.fetchUserNameById(meta.created_by);
          createdByDisplay = resolved || String(meta.created_by);
        }

        // IMPORTANT: keep permissions on studyMeta for Option A checks
        this.studyMeta = {
          id: meta.id,
          study_name: meta.study_name,
          study_description: meta.study_description,
          created_by_id: meta.created_by,
          created_by_display: createdByDisplay || "-",
          created_by_email: meta.created_by_email || "",
          created_at: meta.created_at,
          updated_at: meta.updated_at,
          permissions: meta.permissions || null,
          status: meta.status || "PUBLISHED",
          archived_from_status: meta.archived_from_status || null,
        };
        this.studyData = sd || {};

        // If user doesn't have edit permission, prevent landing on edit tab after refresh
        if (!this.visibleTabs.some((tab) => tab.key === this.activeTab)) {
          this.activeTab = "meta";
        }
      } catch (e) {
        if (this.isMaintenanceError(e)) {
          this.maintenanceMessage = this.apiErrorDetail(e) || "eCRF is under maintenance. Please try again later.";
          return;
        }
        console.error("Failed to fetch study view:", e);
      }
    },
    async fetchStudyFiles() {
      const token = this.token;
      if (!token) return;
      try {
        const { data } = await axios.get(`/forms/studies/${this.studyId}/files`, { headers: { Authorization: `Bearer ${token}` } });
        this.allFiles = Array.isArray(data) ? data : [];
      } catch (e) {
        if (this.isMaintenanceError(e)) {
          this.maintenanceMessage = this.apiErrorDetail(e) || "eCRF is under maintenance. Please try again later.";
          return;
        }
        console.warn("Failed to fetch study files:", e?.response?.data || e.message);
      }
    },

    apiErrorDetail(e) {
      const detail = e?.response?.data?.detail;
      if (Array.isArray(detail)) return detail.map((d) => d?.msg || String(d)).join(", ");
      if (detail) return String(detail);
      return "";
    },

    isMaintenanceError(e) {
      return e?.response?.status === 503;
    },

    async fetchVersionsAndDiffs() {
      const token = this.token;
      if (!token) return;
      let shouldLoadVisibleDetails = false;
      this.versionsLoading = true;
      this.canShowVersionDetails = true;
      try {
        const { data } = await axios.get(`/forms/studies/${this.studyId}/versions`, { headers: { Authorization: `Bearer ${token}` } });
        const list = Array.isArray(data) ? data : [];
        this.versions = [...list].sort((a, b) => Number(a.version) - Number(b.version));
        this.versionSchemas = {};
        this.versionDiffs = {};
        this.versionDetailsLoaded = this.versions.length === 0;
        shouldLoadVisibleDetails = !this.metaCollapsed.versions && this.versions.length > 0;
      } catch (e) {
        console.error("fetchVersionsAndDiffs failed:", e?.response?.data || e.message);
        this.versions = [];
        this.versionSchemas = {};
        this.versionDiffs = {};
        this.versionDetailsLoaded = false;
      } finally {
        this.versionsLoading = false;
      }
      if (shouldLoadVisibleDetails) await this.fetchVersionDiffDetails();
    },

    async fetchVersionDiffDetails() {
      const token = this.token;
      if (!token || this.versionDetailsLoaded || this.versionsLoading || !this.versions.length) return;

      this.versionsLoading = true;
      this.canShowVersionDetails = true;
      try {
        const schemas = {};
        for (const v of this.versions) {
          try {
            const resp = await axios.get(`/forms/studies/${this.studyId}/template`, {
              params: { version: v.version }, headers: { Authorization: `Bearer ${token}` }
            });
            schemas[v.version] = resp.data?.schema || {};
          } catch (e) {
            if (e?.response?.status === 403) { this.canShowVersionDetails = false; break; }
            throw e;
          }
        }
        this.versionSchemas = schemas;

        if (!this.canShowVersionDetails) {
          this.versionDiffs = {};
          this.versionDetailsLoaded = true;
          return;
        }

        const diffs = {};
        for (const v of this.versions) {
          const ver = Number(v.version);
          if (ver <= 1) continue;
          const previousSchema = schemas[ver - 1];
          const nextSchema = schemas[ver];
          if (previousSchema && nextSchema) {
            const diff = computeTemplateDiff(previousSchema, nextSchema);
            diff.summary = buildVersionSummary(diff);
            diffs[ver] = diff;
          }
        }
        this.versionDiffs = diffs;
        this.versionDetailsLoaded = true;
      } catch (e) {
        console.error("fetchVersionDiffDetails failed:", e?.response?.data || e.message);
        this.versionSchemas = {};
        this.versionDiffs = {};
      } finally {
        this.versionsLoading = false;
      }
    },

    async ensureAccessData() {
      await Promise.all([this.fetchAccessList(), this.fetchAllUsers()]);
    },

    async fetchAccessList() {
      const token = this.token;
      if (!token) return;
      try {
        const { data } = await axios.get(`/forms/studies/${this.studyId}/access`, { headers: { Authorization: `Bearer ${token}` } });
        const list = Array.isArray(data) ? data : (data?.items || []);
        this.accessList = list.map((g) => ({
          user_id: g.user_id,
          role: g.role || "Investigator",
          email: g.email || g.user_email || "",
          username: g.username || "",
          display_name: g.display_name || g.user_display || "",
          granted_by: g.created_by,
          granted_by_display: g.created_by_display || "",
          created_at: g.created_at,
          permissions: {
            view: !!(g.permissions?.view ?? true),
            add_data: !!(g.permissions?.add_data ?? true),
            edit_study: !!(g.permissions?.edit_study ?? false),
          },
        }));
      } catch (e) {
        console.warn("fetchAccessList failed:", e?.response?.data || e.message);
        this.accessList = [];
      }
    },
    async fetchAllUsers() {
      const token = this.token;
      if (!token) return;
      try {
        const { data } = await axios.get(`/users/admin/users`, { headers: { Authorization: `Bearer ${token}` } });
        const arr = Array.isArray(data) ? data : (data?.items || []);
        this.allUsers = arr;
      } catch (e) {
        console.warn("fetchAllUsers failed:", e?.response?.data || e.message);
        this.allUsers = [];
      }
    },
    async grantAccess() {
      if (!this.selectedUserId) return;

      this.granting = true;
      const token = this.token;

      try {
        const payload = {
          user_id: Number(this.selectedUserId),
          permissions: {
            view: !!this.permView,
            add_data: !!this.permAdd,
            edit_study: !!this.permEdit,
          },
        };

        await axios.post(
          `/forms/studies/${this.studyId}/access`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        this.selectedUserId = "";
        this.permView = true;
        this.permAdd = true;
        this.permEdit = false;

        await this.fetchAccessList();
      } catch (e) {
        console.error("grantAccess failed:", e?.response?.data || e.message);
      } finally {
        this.granting = false;
      }
    },

    async revokeAccess(g) {
      if (!g || g.user_id == null) return;
      const token = this.token;
      this.$set ? this.$set(this.revokeBusy, g.user_id, true) : (this.revokeBusy = { ...this.revokeBusy, [g.user_id]: true });
      try {
        await axios.delete(`/forms/studies/${this.studyId}/access/${g.user_id}`, { headers: { Authorization: `Bearer ${token}` } });
        await this.fetchAccessList();
      } catch (e) {
        console.error("revokeAccess failed:", e?.response?.data || e.message);
      } finally {
        const next = { ...this.revokeBusy };
        delete next[g.user_id];
        this.revokeBusy = next;
      }
    },

    // shared loader (kept)
    async loadStudyIntoStoreForEdit() {
      localStorage.removeItem("setStudyDetails");
      localStorage.removeItem("scratchForms");

      const token = this.token;
      if (!token) { this.$router.push("/login"); return false; }

      try {
        const resp = await axios.get(`/forms/studies/${this.studyId}`, { headers: { Authorization: `Bearer ${token}` } });
        const sd = resp.data.content?.study_data;
        const meta = resp.data.metadata || {};
        if (!sd) { alert("Study content is empty."); return false; }

        let assignments = Array.isArray(sd.assignments) ? sd.assignments : [];
        if (!assignments.length && sd.selectedModels?.length) {
          const m = sd.selectedModels.length;
          const v = sd.visits?.length || 0;
          const g = sd.groups?.length || 0;
          assignments = Array.from({ length: m }, () =>
            Array.from({ length: v }, () =>
              Array.from({ length: g }, () => false)
            )
          );
        }

        const studyInfo = {
          id: meta.id,
          name: meta.study_name,
          description: meta.study_description,
          created_at: meta.created_at,
          updated_at: meta.updated_at,
          created_by: meta.created_by,
          status: meta.status,
        };

        const normalizedForms =
          Array.isArray(sd.forms) && sd.forms.length
            ? sd.forms.map((form) => ({
                ...JSON.parse(JSON.stringify(form)),
                sections: Array.isArray(form.sections)
                  ? form.sections.map((section) => ({
                      ...section,
                      fields: Array.isArray(section.fields)
                        ? section.fields.map((field) => ({
                            ...field,
                            constraints: field?.constraints || {}
                          }))
                        : []
                    }))
                  : [],
                logic: {
                  version: form?.logic?.version || 1,
                  calculations: Array.isArray(form?.logic?.calculations) ? form.logic.calculations : [],
                  conditions: Array.isArray(form?.logic?.conditions) ? form.logic.conditions : [],
                  valueAssignments: Array.isArray(form?.logic?.valueAssignments) ? form.logic.valueAssignments : []
                }
              }))
            : (sd.selectedModels ? [{
                sections: sd.selectedModels.map(model => ({
                  ...JSON.parse(JSON.stringify(model)),
                  title: model.title,
                  fields: Array.isArray(model.fields)
                    ? model.fields.map((field) => ({
                        ...field,
                        constraints: field?.constraints || {}
                      }))
                    : [],
                  source: model.source || "template"
                })),
                logic: {
                  version: 1,
                  calculations: [],
                  conditions: [],
                  valueAssignments: []
                }
              }] : []);


        this.$store.commit("setStudyDetails", {
          study_metadata: studyInfo,
          study: { id: meta.id, ...sd.study },
          groups: sd.groups || [],
          visits: sd.visits || [],
          subjectCount: sd.subjectCount || 0,
          assignmentMethod: sd.assignmentMethod || "random",
          subjectIdConfig: sd.subjectIdConfig || null,
          subjects: sd.subjects || [],
          assignments: assignments,
          forms: normalizedForms
        });

        localStorage.setItem("scratchForms", JSON.stringify(normalizedForms));

        return true;
      } catch (e) {
        console.error("Failed to load study details:", e);
        alert("Failed to load study details.");
        return false;
      }
    },

    // open single step edit
    async openEditStep(stepNumber) {
  if (!this.canEditStudy) return;

  const ok = await this.loadStudyIntoStoreForEdit();
  if (!ok) return;

  const step = Number(stepNumber);
  this.$router.push({
    name: "CreateStudy",
    params: { id: this.studyId },
    query: {
      mode: "edit",
      single: "true",
      step: String(step),
      returnTo: "StudyView",
      returnId: String(this.studyId),
      returnTab: "edit",
    },
  });
},

    // attach handlers
    onAttachTempChange(val) {
      this.attachTempValue = Array.isArray(val) ? val : [];
    },
    onFilesSelected(files) {
      const arr = Array.isArray(files) ? files : (files ? [files] : []);
      if (!arr.length) return;
      const existing = new Set(this.pendingFiles.map((p) => `${p.file.name}|${p.file.size}|${p.file.lastModified || ""}`));
      const next = [];
      for (const f of arr) {
        const key = `${f.name}|${f.size}|${f.lastModified || ""}`;
        if (!existing.has(key)) next.push({ key, file: f, description: "" });
      }
      if (next.length) this.pendingFiles = this.pendingFiles.concat(next);
    },
    removePending(idx) {
      if (idx >= 0 && idx < this.pendingFiles.length) this.pendingFiles.splice(idx, 1);
    },
    async saveStudyAttachments() {
      if (!this.pendingFiles.length) return;
      const token = this.token;
      if (!token) { this.$router.push("/login"); return; }
      this.uploading = true;
      try {
        for (const item of this.pendingFiles) {
          const fd = new FormData();
          fd.append("uploaded_file", item.file);
          fd.append("description", item.description || "");
          fd.append("storage_option", "local");
          fd.append("modalities_json", "[]");
          await axios.post(
            `/forms/studies/${this.studyId}/files`,
            fd,
            { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
          );
        }
        this.pendingFiles = [];
        this.attachTempValue = [];
        await this.fetchStudyFiles();
      } catch (e) {
        console.error("Save attachments failed:", e?.response?.data || e.message);
      } finally {
        this.uploading = false;
      }
    },

    openRevokeDialog(g) {
      this.confirm = { visible: true, target: g };
      this.$nextTick(() => {
        try {
          const el = this.$el.querySelector('.modal');
          el && el.focus();
        } catch (err) {
          console.warn('openRevokeDialog: failed to focus modal', err);
        }
      });
    },
    cancelRevoke() { this.confirm = { visible: false, target: null }; },
    async confirmRevoke() {
      const g = this.confirm.target;
      if (!g) return;
      await this.revokeAccess(g);
      this.cancelRevoke();
    },

    async fetchBidsPath() {
      const token = this.token;
      if (!token) return;

      try {
        const { data } = await axios.get(
          `/forms/studies/${this.studyId}/bids_path`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        this.bidsDatasetPath = data?.dataset_path || "";
        this.bidsPathExists = !!data?.exists;
      } catch (e) {
        console.warn("Failed to fetch BIDS path:", e?.response?.data || e.message);
        this.bidsDatasetPath = "";
        this.bidsPathExists = false;
      }
    },

    async openBidsFolder() {
      if (!this.bidsDatasetPath || !this.bidsPathExists) return;

      const token = this.token;
      if (!token) { this.$router.push("/login"); return; }

      this.openingBids = true;
      try {
        await axios.post(
          `/forms/studies/${this.studyId}/bids_open`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (e) {
        console.error("Failed to open BIDS folder:", e?.response?.data || e.message);
      } finally {
        this.openingBids = false;
      }
    },
  },
  async mounted() {
    this.studyLoading = true;
    this.sharedStudyDataReady = false;
    try {
      this.scrollToTop?.();

      const qTab = this.$route.query?.tab;
      if (qTab && this.tabs.some(t => t.key === qTab)) this.activeTab = qTab;

      await this.fetchMe();
      await this.fetchStudy();
      if (this.maintenanceMessage) return;

      // After we know perms, ensure we don't stay on edit without rights
      if (!this.visibleTabs.some((tab) => tab.key === this.activeTab)) this.activeTab = "meta";

      if (this.canSeeBidsButton) await this.fetchBidsPath();
      await Promise.all([this.fetchStudyFiles(), this.fetchVersionsAndDiffs()]);
      this.sharedStudyDataReady = true;
    } finally {
      this.studyLoading = false;
    }
  },
  beforeRouteEnter(to, from, next) { next((vm) => vm.scrollToTop?.()); },
  beforeRouteUpdate(to, from, next) {
    if (String(to.params.id) === String(from.params.id)) {
      const qTab = to.query?.tab;
      if (qTab && this.tabs.some(t => t.key === qTab) && qTab !== this.activeTab) {
        this.activeTab = qTab;
      }
      next();
      return;
    }

    this.studyLoading = true;
    this.sharedStudyDataReady = false;
    this.maintenanceMessage = "";
    this.studyId = to.params.id;

    const qTab = to.query?.tab;
    if (qTab && this.tabs.some(t => t.key === qTab)) this.activeTab = qTab;
    this.viewDataInitialized = this.activeTab === "viewdata";

    this.scrollToTop?.();
    Promise.resolve()
      .then(() => this.fetchStudy())
      .then(() => {
        if (this.maintenanceMessage) return;
        // If the route query wants edit but user can't, force meta
        if (!this.visibleTabs.some((tab) => tab.key === this.activeTab)) this.activeTab = "meta";
      })
      .then(() => {
        if (this.maintenanceMessage) return;
        if (this.canSeeBidsButton) return this.fetchBidsPath();
      })
      .then(() => {
        if (this.maintenanceMessage) return;
        return Promise.all([this.fetchStudyFiles(), this.fetchVersionsAndDiffs()]);
      })
      .finally(() => {
        this.sharedStudyDataReady = !this.maintenanceMessage;
        this.studyLoading = false;
        next();
      });
  },
};
</script>

<style scoped>
/* IMPORTANT: do NOT apply font-family to * (breaks FontAwesome icons).
   Keep typography scoped to the page container only. */
.study-view-layout {
  letter-spacing: 0.1px;
}

.study-view-layout { display: flex; flex-direction: column; gap: 16px; padding: 24px; background: #f9fafb; min-height: 100%; }

.study-loading-overlay {
  position: fixed;
  inset: 0;
  z-index: 5000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(249, 250, 251, 0.82);
  backdrop-filter: blur(2px);
}

.study-loading-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  min-width: 260px;
  padding: 24px 28px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.14);
  color: #111827;
  text-align: center;
}

.load-error-state {
  color: #991b1b;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 16px;
  margin: 24px auto;
  max-width: 520px;
  text-align: center;
  font-weight: 600;
}

.loading {
  text-align: center;
  padding: 50px;
  font-size: 16px;
  color: #6b7280;
}

.study-loading-spinner {
  width: 34px;
  height: 34px;
  border: 4px solid #e5e7eb;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: study-loading-spin 0.8s linear infinite;
}

.study-loading-title {
  font-size: 16px;
  font-weight: 700;
}

.study-loading-message {
  font-size: 14px;
  color: #4b5563;
}

@keyframes study-loading-spin {
  to { transform: rotate(360deg); }
}

/* Fullscreen overlay for View Data */
.study-view-layout.sv-dashboard-fullscreen {
  position: fixed;
  inset: 0;
  padding: 0;
  gap: 0;
  background: #fff;
  z-index: 3000;
}

/* Header */
.sv-header { position: relative; display: grid; grid-template-columns: 1fr auto 1fr; align-items: center; gap: 16px; }
.back-btn { position: absolute; left: 0; top: 50%; transform: translateY(-50%); }
.title-wrap { grid-column: 2 / 3; min-width: 0; text-align: center; }
.sv-title { margin: 0; font-size: 22px; font-weight: 800; color: #111827; }
.sv-title-row { display: flex; align-items: center; justify-content: center; gap: 10px; }
.archived-badge { padding: 4px 9px; border: 1px solid #94a3b8; border-radius: 999px; background: #f1f5f9; color: #334155; font-size: 11px; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; }
.sv-subtitle { margin: 6px 0 0; color: #6b7280; }
.header-spacer { width: 56px; height: 1px; }

/* Card layout */
.card { display: grid; grid-template-columns: 240px 1fr; background: #fff; border: 1px solid #ececec; border-radius: 14px; box-shadow: 0 6px 18px rgba(0,0,0,0.04); overflow: hidden; }

/* When viewdata and sidebar collapsed */
.card.sv-sidebar-collapsed { grid-template-columns: 52px 1fr; }

/* When fullscreen, no sidebar column */
.card.sv-dashboard-fullscreen { grid-template-columns: 1fr; border-radius: 0; border: none; box-shadow: none; height: 100vh; }

/* Vertical tabs */
.v-tabs { background: #fcfcff; border-right: 1px solid #f0f0f6; padding: 10px; display: flex; flex-direction: column; gap: 8px; min-height: 360px; }
.v-tabs.collapsed { padding: 8px 6px; gap: 8px; }

.v-tabs-tools { display: flex; justify-content: center; padding-bottom: 6px; }

/* Tabs */
.v-tab { text-align: left; background: #fff; border: 1px solid #ececec; border-radius: 10px; padding: 10px 12px; font-size: 14px; color: #374151; cursor: pointer; transition: background .2s, color .2s, box-shadow .2s, border .2s; }
.v-tabs.collapsed .v-tab { text-align: center; padding: 10px 8px; font-size: 12px; }
.v-tab:hover { background: #f8fafc; }
.v-tab.active { background: #eef2ff; color: #3538cd; border-color: #e0e7ff; box-shadow: 0 3px 10px rgba(53,56,205,0.08); }

/* Panel */
.v-panel { padding: 18px 20px; }
.v-panel-tight { padding: 0; overflow: hidden; min-height: 0; }

/* Viewdata host fills */
.viewdata-host { height: 100%; min-height: 0; }

/* Panel titles */
.panel-title { margin: 4px 0 12px; font-size: 16px; font-weight: 700; }
.panel-title.center { text-align: center; }

/* Subsections */
.subsection { margin-top: 18px; }
.sub-title { margin: 0; font-size: 14px; font-weight: 700; color: #111827; }

/* Meta grid */
.meta-grid { display: grid; grid-template-columns: repeat(2, minmax(220px, 1fr)); gap: 12px; }
.meta-item { border: 1px solid #f1f1f1; border-radius: 10px; padding: 10px 12px; background: #fff; }
.meta-label { font-size: 12px; text-transform: uppercase; letter-spacing: 0.04em; color: #6b7280; margin-bottom: 6px; }
.meta-value { font-size: 14px; color: #111827; word-break: break-word; }

/* Collapsible meta sections: consistent containers */
.meta-sections { margin-top: 14px; display: flex; flex-direction: column; gap: 12px; }

.meta-section {
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 12px;
}

.meta-section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.meta-section-body { margin-top: 10px; }

.meta-toggle {
  padding: 6px 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* Key-value grids */
.kv-grid { display: grid; grid-template-columns: 1fr; gap: 8px; }
.kv-row { display: grid; grid-template-columns: 200px 1fr; gap: 8px; padding: 8px 10px; border: 1px solid #f5f5f5; border-radius: 8px; background: #fff; }
.kv-key { color: #6b7280; font-size: 13px; }
.kv-val { color: #111827; font-size: 14px; white-space: pre-wrap; overflow-wrap: anywhere; word-break: break-word; }

/* Lists */
.list-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(360px, 1fr)); gap: 10px; }
.list-card { border: 1px solid #f1f1f1; border-radius: 12px; padding: 10px; background: #fff; }
.list-card .kv-row { display: grid; grid-template-columns: minmax(120px, 220px) 1fr; gap: 8px; align-items: start; }

/* per-subject table */
.per-subject-block { margin-top: 14px; }
.per-subject-title { margin: 8px 0; font-size: 13px; font-weight: 700; color: #111827; }
.ps-table-wrap { overflow: auto; border: 1px solid #f1f1f1; border-radius: 10px; background: #fff; }
.ps-table { width: 100%; border-collapse: collapse; font-size: 13px; background: #fff; }
.ps-table th, .ps-table td { border-bottom: 1px solid #f5f5f5; padding: 8px 10px; text-align: left; }
.ps-table th { background: #fafafe; color: #374151; font-weight: 600; }
.ps-table tr:last-child td { border-bottom: none; }

/* Docs */
.doc-list { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 10px; }
.doc-item { border: 1px solid #f1f1f1; border-radius: 12px; padding: 12px; background: #fff; }
.doc-name { font-weight: 700; word-break: break-word; }
.doc-meta { font-size: 12px; margin: 6px 0 8px; color: #6b7280; }
.doc-desc { margin-top: 4px; font-size: 13px; color: #374151; }
.doc-description-edit { margin-top: 8px; display: flex; flex-direction: column; gap: 6px; }
.doc-description-edit label { font-size: 12px; color: #6b7280; }
.doc-edit-actions { display: flex; gap: 8px; }

.doc-path { margin-top: 6px; font-size: 12px; color: #6b7280; display: flex; gap: 6px; align-items: flex-start; }
.doc-path .label { flex: 0 0 auto; color: #6b7280; }
.doc-path .file-path { flex: 1 1 auto; word-break: break-word; overflow-wrap: anywhere; white-space: normal; }

.doc-actions {
  margin-top: 12px;
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 8px;
}

/* Attach */
.attach-row { margin-bottom: 10px; }
.pending-list { display: grid; grid-template-columns: 1fr; gap: 10px; }
.pending-item { border: 1px solid #f1f1f1; border-radius: 10px; padding: 10px; background: #fff; display: flex; flex-direction: column; gap: 6px; }
.pi-head { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.pi-name { font-weight: 600; min-width: 0; word-break: break-word; }
.pi-size { color: #6b7280; font-size: 12px; }
.icon-inline { border:none; background:transparent; padding:6px; border-radius:8px; cursor:pointer; }
.icon-inline.danger { color:#b91c1c; }
.pi-desc-label { font-size: 12px; color: #6b7280; }
.pi-desc-input { width: 100%; padding: 8px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 14px; box-sizing: border-box; }
.attach-actions { margin-top: 10px; }

/* Buttons */
.btn-primary { border-radius: 10px; padding: 10px 12px; font-size: 14px; cursor: pointer; transition: transform .05s, box-shadow .2s, background .2s, color .2s, border .2s; border: 1px solid transparent; background: #111827; color: #fff; }
.btn-primary[disabled] { opacity: 0.6; cursor: not-allowed; }
.btn-primary:hover:not([disabled]) { transform: translateY(-1px); box-shadow: 0 6px 18px rgba(0,0,0,.1); }
.btn-minimal { background: none; border: 1px solid #e0e0e0; border-radius: 8px; padding: 8px 12px; font-size: 14px; color: #555; cursor: pointer; transition: background 0.3s ease, color 0.3s ease, border-color 0.3s ease; }
.btn-minimal:hover { background: #e8e8e8; color: #000; border-color: #d6d6d6; }
.btn-minimal.icon-only { padding: 8px 10px; }
.btn-minimal.danger { color: #b91c1c; border-color: #fecaca; }
.btn-minimal.danger:hover { color: #991b1b; background: #fef2f2; border-color: #fca5a5; }
.btn-primary.compact, .btn-minimal.compact { padding: 6px 10px; font-size: 13px; }

/* Empty + util */
.empty-state { color: #6b7280; }
.center { text-align: center; }
.strong { font-weight: 600; }
.muted { color: #6b7280; }
.monospace { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace; }

/* Access table */
.table-wrap { overflow: auto; border: 1px solid #f1f1f1; border-radius: 12px; background: #fff; }
.access-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.access-table th, .access-table td { padding: 10px 12px; border-bottom: 1px solid #f6f6f6; text-align: left; }
.access-table thead th { background: #fafafe; color: #374151; font-weight: 600; }
.access-table tr:last-child td { border-bottom: none; }
.owner-row { background: #fcfcff; }
.pill { display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 12px; margin-right: 6px; border: 1px solid #e5e7eb; color: #6b7280; background: #fff; }
.pill.ok { color: #065f46; background: #ecfdf5; border-color: #a7f3d0; }
.pill.muted { color: #6b7280; background: #f9fafb; }
.tag { margin-left: 8px; font-size: 11px; border-radius: 6px; padding: 2px 6px; background: #eef2ff; color: #3538cd; border: 1px solid #e0e7ff; }
.btn-link { background: transparent; border: none; padding: 0; cursor: pointer; text-decoration: underline; }
.btn-link.danger { color: #b91c1c; }

/* Grant form */
.grant-grid {
  display: grid; grid-template-columns: repeat(3, minmax(180px, 1fr)); gap: 12px;
  background: #fff; border: 1px solid #f1f1f1; border-radius: 12px; padding: 12px; align-items: start;
}
.field { display: flex; flex-direction: column; gap: 6px; }
.label { font-size: 12px; text-transform: uppercase; letter-spacing: .04em; color: #6b7280; }
.input { width: 100%; height: 40px; padding: 8px 10px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 14px; box-sizing: border-box; background: #fff; }
.input-select { appearance: none; -webkit-appearance: none; -moz-appearance: none; background-position: right 10px center; }
.help { font-size: 12px; color: #6b7280; }
.actions { display: flex; gap: 8px; align-self: end; }

/* Modal */
.modal-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,.35); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.modal { background: #fff; border-radius: 12px; padding: 16px; width: 100%; max-width: 420px; box-shadow: 0 10px 30px rgba(0,0,0,.15); outline: none; }
.modal-title { margin: 0 0 8px; font-size: 16px; font-weight: 700; color: #111827; }
.modal-text { margin: 0 0 14px; color: #374151; font-size: 14px; }
.modal-actions { display: flex; justify-content: flex-end; gap: 8px; }
.btn-primary.danger { background: #b91c1c; border-color: #a11a1a; }
.btn-primary.danger:hover { background: #991b1b; }

/* Versions */
.versions-wrap { display: block; }
.version-list { display: grid; grid-template-columns: 1fr; gap: 10px; }
.version-card { border: 1px solid #f1f1f1; border-radius: 12px; padding: 12px; background: #fff; }
.vc-head { display: flex; flex-direction: column; gap: 4px; }
.vc-title { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.vc-date { color: #6b7280; font-size: 12px; }
.vc-summary { color: #374151; font-size: 13px; margin-top: 2px; }
.vc-summary-line { display: block; margin-top: 2px; }
.diff-scroll { overflow: auto; border: 1px solid #f1f1f1; border-radius: 10px; padding: 12px; background: #fff; }

.bids-location .file-path { word-break: break-all; }
.bids-actions { margin-top: 8px; }

/* edit steps grid */
.edit-steps-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(260px, 1fr));
  gap: 12px;
  margin-top: 12px;
}
.edit-step-card {
  text-align: left;
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 12px;
  padding: 12px 14px;
  cursor: pointer;
  transition: background .2s, border .2s, box-shadow .2s, transform .05s;
}
.edit-step-card:hover:not([disabled]) { background: #f8fafc; border-color: #e5e7eb; box-shadow: 0 8px 18px rgba(0,0,0,0.06); transform: translateY(-1px); }
.edit-step-card[disabled] { opacity: 0.6; cursor: not-allowed; }
.esc-title { font-weight: 800; color: #111827; margin-bottom: 4px; }
.esc-desc { color: #6b7280; font-size: 13px; }
.edit-hint { margin-top: 10px; text-align: center; }
/* ============================================================
   VIEW DATA FULLSCREEN HEIGHT FIX
   Ensures the embedded dashboard receives real available height
   when the enlarge / fit-to-screen button is clicked.
   ============================================================ */

.study-view-layout.sv-dashboard-fullscreen {
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  min-height: 0;
  padding: 0;
  gap: 0;
  background: #fff;
  z-index: 3000;
  overflow: hidden;
}

.study-view-layout.sv-dashboard-fullscreen > .sv-content.card {
  width: 100%;
  height: 100%;
  min-height: 0;
  border-radius: 0;
  border: none;
  box-shadow: none;
  overflow: hidden;
  grid-template-columns: 1fr;
}

.study-view-layout.sv-dashboard-fullscreen .v-panel {
  width: 100%;
  height: 100%;
  min-height: 0;
  padding: 0;
  overflow: hidden;
}

.study-view-layout.sv-dashboard-fullscreen .v-panel-tight {
  width: 100%;
  height: 100%;
  min-height: 0;
  padding: 0;
  overflow: hidden;
}

.study-view-layout.sv-dashboard-fullscreen .viewdata-host {
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.study-view-layout.sv-dashboard-fullscreen .viewdata-host > * {
  width: 100%;
  height: 100%;
  min-height: 0;
}
/* ============================================================
   VIEW DATA NORMAL MODE HEIGHT CONTAINMENT
   Required so StudyDataDashboard's table-wrapper becomes the
   actual vertical scroll container.
   ============================================================ */

.card.sv-viewdata {
  height: calc(100vh - 120px);
  min-height: 520px;
  overflow: hidden;
}

.card.sv-viewdata.sv-sidebar-collapsed {
  height: calc(100vh - 120px);
  min-height: 520px;
  overflow: hidden;
}

.card.sv-viewdata .v-panel-tight {
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.card.sv-viewdata .viewdata-host {
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.card.sv-viewdata .viewdata-host > * {
  height: 100%;
  min-height: 0;
}

/* Compliance keeps Study View navigation fixed and scrolls only its panel. */
.card.sv-compliance {
  height: calc(100vh - 120px);
  min-height: 520px;
  overflow: hidden;
}

.card.sv-compliance .v-tabs {
  height: 100%;
  min-height: 0;
  overflow-y: auto;
}

.card.sv-compliance .v-panel-compliance {
  height: 100%;
  min-width: 0;
  min-height: 0;
  box-sizing: border-box;
  padding-bottom: 36px;
  overflow-y: auto;
  overscroll-behavior: contain;
  scroll-padding-bottom: 36px;
  scrollbar-gutter: stable;
}
/* Responsive */
@media (max-width: 900px) {
  .edit-steps-grid { grid-template-columns: 1fr; }
}
.bids-actions.export-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
}

.bids-actions.export-actions .btn-primary {
  margin: 0;
}
.study-export-grid { display: grid; grid-template-columns: minmax(320px, 1.1fr) minmax(300px, .9fr); gap: 12px; margin-top: 16px; }
.main-export-option { display: flex; gap: 14px; min-height: 166px; padding: 18px; text-align: left; border: 1px solid #111827; border-radius: 12px; background: #f9fafb; color: #111827; cursor: pointer; transition: background .2s, box-shadow .2s, transform .05s; }
.main-export-option:hover:not(:disabled) { background: #f3f4f6; box-shadow: 0 6px 18px rgba(0,0,0,.08); transform: translateY(-1px); }.main-export-option:disabled { opacity: .6; cursor: wait; }
.export-option-icon { display: grid; place-items: center; flex: 0 0 44px; height: 44px; border-radius: 9px; background: #111827; color: #fff; font-size: 16px; }.export-option-copy { display: flex; flex-direction: column; align-items: flex-start; }.export-option-label { margin-bottom: 4px; color: #6b7280; font-size: 10px; font-weight: 700; letter-spacing: .08em; }.export-option-copy strong { font-size: 18px; }.export-option-copy small { margin-top: 6px; color: #4b5563; font-size: 12px; line-height: 1.5; }.export-option-action { margin-top: auto; padding-top: 12px; font-size: 12px; font-weight: 700; }.export-option-action i { margin-left: 5px; font-size: 10px; }
.secondary-export-options { display: flex; flex-direction: column; gap: 10px; }.secondary-export-options button { display: grid; grid-template-columns: 38px 1fr auto; gap: 10px; align-items: center; min-height: 78px; padding: 12px; text-align: left; border: 1px solid #e0e0e0; border-radius: 10px; background: #fff; color: #111827; cursor: pointer; transition: background .2s, border-color .2s; }.secondary-export-options button:hover:not(:disabled) { border-color: #b8bcc3; background: #f9fafb; }.secondary-export-options button:disabled { opacity: .6; cursor: wait; }.secondary-export-options strong, .secondary-export-options small { display: block; }.secondary-export-options strong { font-size: 13px; }.secondary-export-options small { margin-top: 3px; color: #6b7280; font-size: 11px; line-height: 1.35; }.secondary-export-options > button > i { color: #9ca3af; font-size: 11px; }.secondary-export-icon { display: grid; place-items: center; width: 36px; height: 36px; border: 1px solid #e0e0e0; border-radius: 8px; background: #f3f4f6; color: #374151; }.export-permission-note { grid-column: 1 / -1; }
@media (max-width: 820px) { .study-export-grid { grid-template-columns: 1fr; } }
.edit-danger-zone {
  margin-top: 18px;
  display: flex;
  justify-content: flex-start;
  padding-left: 2px;
  gap: 10px;
  flex-wrap: wrap;
}

.archive-study-btn { border-color: #94a3b8; color: #334155; }
.archive-state-card { max-width: 680px; margin: 16px auto 0; padding: 24px; border: 1px solid #cbd5e1; border-radius: 12px; background: #f8fafc; text-align: center; }
.archive-state-card h3 { margin: 0 0 8px; color: #0f172a; }
.archive-state-card p { margin: 0 auto 18px; max-width: 560px; color: #475569; line-height: 1.55; }

.btn-danger {
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 14px;
  cursor: pointer;
  border: 1px solid #b91c1c;
  background: #b91c1c;
  color: #fff;
  transition: background .2s, border-color .2s, box-shadow .2s, transform .05s;
}

.btn-danger:hover:not([disabled]) {
  background: #991b1b;
  border-color: #991b1b;
  box-shadow: 0 6px 18px rgba(185, 28, 28, 0.18);
  transform: translateY(-1px);
}

.btn-danger[disabled] {
  opacity: 0.6;
  cursor: not-allowed;
}
.access-grant-grid {
  grid-template-columns: minmax(260px, 1.2fr) minmax(280px, 1fr) auto;
}

.grant-user-field,
.grant-perm-field {
  min-width: 0;
}

.grant-actions {
  align-self: end;
}

.perm-row {
  display: flex;
  gap: 18px;
  flex-wrap: wrap;
  min-height: 40px;
  align-items: center;
}

.chk {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  user-select: none;
  color: #111827;
  font-size: 14px;
}

.chk input {
  transform: translateY(1px);
}

@media (max-width: 900px) {
  .access-grant-grid {
    grid-template-columns: 1fr;
  }

  .grant-actions {
    align-self: start;
  }
}
</style>
